/**
 * 全局：筛选区自动触发「查询」
 *
 * 凡是用 `.standard-filter-form` 类的筛选 el-form：
 * 1) 任意输入框（el-input、el-input-number、el-date-picker 的 input 等）按回车 → 触发查询
 * 2) 单选 el-select 选完一项（含点击 × 清空）→ 触发查询；多选不触发，避免用户连选多项被打断
 *
 * 触发动作 = 点击该表单内的主按钮 `.el-button--primary`（约定为「查询」）。
 * 所有页面无需单独绑定。
 */

const FORM_SEL = '.standard-filter-form';
const PRIMARY_BTN_SEL = '.el-button--primary';

const fireSearch = (form: Element): void => {
  const btn = form.querySelector<HTMLElement>(PRIMARY_BTN_SEL);
  if (btn) btn.click();
};

const isSearchableInput = (el: EventTarget | null): el is HTMLInputElement => {
  if (!el || (el as HTMLElement).tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return ['text', 'search', 'tel', 'url', 'email', 'number', 'password'].includes(type);
};

/** 回车触发：在筛选 form 内任意 input 上 keydown.Enter → click 查询 */
const setupEnterToSearch = (): void => {
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.isComposing) return;
    if (!isSearchableInput(e.target)) return;
    const form = (e.target as HTMLElement).closest(FORM_SEL);
    if (!form) return;
    e.preventDefault();
    fireSearch(form);
  });
};

/**
 * 单选 select 选完后触发：
 * - mousedown 阶段记录用户当前点击的 select 是否为「单选 + 在筛选 form 内」
 * - 随后点击 dropdown 选项 / 触发器内的 × 清空时再触发查询
 * 多选 select（带 .el-select__tags 容器）跳过，避免连选打断
 */
const setupSelectChangeSearch = (): void => {
  let pendingForm: Element | null = null;

  document.addEventListener(
    'mousedown',
    (e) => {
      const target = e.target as HTMLElement;
      // 点击在已展开的下拉浮层内：选项被 teleport 到 body（在 .el-select-dropdown 内、
      // 不在 .el-select 内），此时保留触发器按下时记录的 pendingForm，待 click 选项再触发查询
      if (target.closest('.el-select-dropdown')) return;
      const wrapper = target.closest<HTMLElement>('.el-select');
      if (!wrapper) {
        pendingForm = null;
        return;
      }
      // 多选跳过：保留用户连续勾选体验
      const isMultiple =
        wrapper.classList.contains('el-select--multiple') ||
        !!wrapper.querySelector('.el-select__tags');
      if (isMultiple) {
        pendingForm = null;
        return;
      }
      pendingForm = wrapper.closest(FORM_SEL);
    },
    true,
  );

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    // 命中：选项 或 trigger 上的清空按钮（× 圆形 icon，hover 时显示）
    const isItem = !!target.closest('.el-select-dropdown__item');
    const isClear = !!target.closest('.el-icon.is-circle-close, .clear-icon');
    if (!isItem && !isClear) return;
    const form = pendingForm;
    pendingForm = null;
    if (!form) return;
    // v-model 更新发生在 click 后续微任务，延后触发以拿到新值
    setTimeout(() => fireSearch(form), 0);
  });
};

/** 在 main.ts 启动时调用一次即可：注册全局 keydown / click 监听 */
export function setupEnterSearch(): void {
  setupEnterToSearch();
  setupSelectChangeSearch();
}
